/*
 * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT
 *
 * Licensed under the MIT License. See the LICENSE accompanying this file
 * for the specific language governing permissions and limitations under
 * the License.
 */

import React from "react";
import ReactDOM from "react-dom";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import { Provider } from 'react-redux';
import store from './redux/store';
import { Amplify, Auth } from "aws-amplify";

import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import App from './App';
import Dashboard from './pages/Dashboard/Dashboard';
import Settings from './pages/Settings/Settings';
import Logout from './pages/Logout/Logout';
import ErrorPage from './pages/ErrorPage/ErrorPage';

import * as serviceWorker from "./serviceWorker";
import awsconfig from "./aws-exports";

import "./index.css";
import { isJapanesePhoneNumber, formatPhoneNumberForJapan } from './utils/phoneNumber';
import { applyBrand } from './utils/applyBrand';
import { injectAmplifyShadowStyles } from './utils/injectShadowStyles';
import { resolveDeviceLabel } from './utils/browserInfo';

var Config = require("Config");

let amplifyConfig = {
	...awsconfig,
	Auth: {
		// OPTIONAL - Manually set the authentication flow type. Default is 'USER_SRP_AUTH'
		authenticationFlowType: Config.authenticationFlowType !== undefined ? Config.authenticationFlowType : "USER_SRP_AUTH",
	},
};

// If we have in parameter that means start a PKCE or Implict flow
// We switch the clientId to submit the one from the client website
let queryStringParams = new URLSearchParams(window.location.search);
// URL の client_id に半角スペース等が混入すると Cognito 認証や brand 判定が壊れるため
// 保存前に trim する（過去に汚染された localStorage は applyBrand 側でも trim される）。
let clientId = queryStringParams.get("client_id")?.trim();
if (clientId) {
	// We save the client ID, our Amplify auth will be based on that one
	localStorage.setItem("client-id", clientId);
} else {
	// If there is no client-Id in query, we set back the last one used for login
	// it may be undefined if we never logged in
	clientId = localStorage.getItem("client-id");
}
if (clientId) {
	// We configure the Amplify Auth component in the context of using a client website client-id
	// if no clientId is found (direct login not from a client) the web client id of the broker will be used as default
	amplifyConfig.aws_user_pools_web_client_id = clientId;
}
Amplify.configure(amplifyConfig);

// ブランド判定（client_id → client_name → brand）と body クラス付与
applyBrand();

// amplify Web Components の shadow DOM に halmek デザイン用 CSS を注入
// （外部 CSS で届かない内部スタイルを上書きするため。詳細は injectShadowStyles.js コメント参照）
injectAmplifyShadowStyles();

// Login Alert API call
const callLoginAlertApi = async (userId, email, phoneNumber, loginUsername) => {
	try {
		// Get API endpoint from aws-exports
		const apiConfig = awsconfig.aws_cloud_logic_custom?.find(
			api => api.name === 'amplifyIdentityBrokerApi'
		);

		if (!apiConfig) {
			console.warn('Login alert API endpoint not configured');
			return;
		}

		const endpoint = `${apiConfig.endpoint}/loginAlert`;

		// ログイン種別の判定
		// - @を含む場合はメールアドレスログイン
		// - +81で始まる or 数字のみの場合は電話番号ログイン
		let loginMethod = 'unknown';
		if (loginUsername) {
			if (loginUsername.includes('@')) {
				loginMethod = 'email';
			} else if (loginUsername.startsWith('+81') || /^[0-9]+$/.test(loginUsername)) {
				loginMethod = 'phone';
			}
		}

		// Chrome 等の UA 削減対策: Client Hints で実バージョンを取得（失敗時はサーバ側 UA 解析）
		const device = await resolveDeviceLabel();

		const response = await fetch(endpoint, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				userId,
				email,
				phoneNumber,
				loginMethod,
				...(device ? { device } : {})
			})
		});

		const result = await response.json();

		if (result.alerts && result.alerts.length > 0) {
			console.log('Login alerts detected:', result.alerts);
			// アラートはログに記録のみ（画面表示なし）
			// 通知はLambda側でSendGrid/SMS経由で送信される
		}
	} catch (error) {
		// ログインアラートAPIのエラーはログイン自体に影響させない
		console.error('Login alert API error:', error);
	}
};

// Wrap Auth.signIn to handle phone number formatting, login alert, and
// user-migration fallback (USER_SRP_AUTH → USER_PASSWORD_AUTH).
const originalSignIn = Auth.signIn.bind(Auth);

// 未移行ユーザーの可能性があると判定するエラーコード。
// wrk 環境の実機挙動を見ながら調整する。NotAuthorizedException を含めることで
// 既存ユーザーのパスワード誤りでも 1 回リトライが走るが、リトライも失敗するだけで
// 副作用はない（初回移行時の取りこぼしを防ぐため広めに取る）。
const MIGRATION_FALLBACK_ERROR_CODES = [
	'UserNotFoundException',
	'NotAuthorizedException',
];

Auth.signIn = async (usernameOrOptions, password, validationData) => {
	let originalUsername;
	let invokeOriginal;

	if (typeof usernameOrOptions === 'object') {
		originalUsername = usernameOrOptions.username;
		if (usernameOrOptions.username && isJapanesePhoneNumber(usernameOrOptions.username)) {
			usernameOrOptions.username = formatPhoneNumberForJapan(usernameOrOptions.username);
		}
		invokeOriginal = () => originalSignIn(usernameOrOptions);
	} else {
		originalUsername = usernameOrOptions;
		let username = usernameOrOptions;
		if (isJapanesePhoneNumber(username)) {
			username = formatPhoneNumberForJapan(username);
		}
		invokeOriginal = () => originalSignIn(username, password, validationData);
	}

	let result;
	try {
		// 1 回目: 既定フロー (USER_SRP_AUTH) で認証
		result = await invokeOriginal();
	} catch (err) {
		if (!MIGRATION_FALLBACK_ERROR_CODES.includes(err && err.code)) {
			throw err;
		}
		// 未移行ユーザーの可能性: USER_PASSWORD_AUTH に切り替えてリトライ。
		// 成功時に移行 Lambda (UserMigration_Authentication) が発火し、
		// Cognito 側にユーザーが自動作成される。
		try {
			Auth.configure({ authenticationFlowType: 'USER_PASSWORD_AUTH' });
			result = await invokeOriginal();
		} finally {
			Auth.configure({ authenticationFlowType: 'USER_SRP_AUTH' });
		}
	}

	// ログイン成功後にログインアラートAPIを呼び出す
	if (result && result.attributes) {
		callLoginAlertApi(
			result.attributes.sub || result.username,
			result.attributes.email,
			result.attributes.phone_number,
			originalUsername
		);
	}

	return result;
};

// Wrap Auth.forgotPassword to handle phone number formatting
const originalForgotPassword = Auth.forgotPassword.bind(Auth);
Auth.forgotPassword = async (username, clientMetadata) => {
	// Format phone number if it's a Japanese local number
	if (isJapanesePhoneNumber(username)) {
		username = formatPhoneNumberForJapan(username);
	}
	return originalForgotPassword(username, clientMetadata);
};

// Wrap Auth.forgotPasswordSubmit to handle phone number formatting
const originalForgotPasswordSubmit = Auth.forgotPasswordSubmit.bind(Auth);
Auth.forgotPasswordSubmit = async (username, code, newPassword, clientMetadata) => {
	// Format phone number if it's a Japanese local number
	if (isJapanesePhoneNumber(username)) {
		username = formatPhoneNumberForJapan(username);
	}
	return originalForgotPasswordSubmit(username, code, newPassword, clientMetadata);
};

/*
 * App Routing
 * With "ProtectedRoute" we redirect user to Login if they are not signed in.
 *
 * If we route to a none existing page we will redirect to the "ErrorPage"
 */
ReactDOM.render(
	<React.StrictMode>
		<Provider store={store}>
			<Router>
				<Switch>
					<ProtectedRoute exact path="/dashboard" component={Dashboard} />
					<ProtectedRoute exact path="/settings/password" component={Settings} />
					<ProtectedRoute exact path="/settings/email" component={Settings} />
					<ProtectedRoute exact path="/settings/phone" component={Settings} />
					<ProtectedRoute exact path="/settings" component={Settings} />
					<Route exact path="/logout" component={Logout} />
					<Route exact path="/" component={App} />
					<Route component={ErrorPage} />
				</Switch>
			</Router>
		</Provider>
	</React.StrictMode>,
	document.getElementById('root')
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://create-react-app.dev/docs/making-a-progressive-web-app
serviceWorker.unregister();
